import React, { useContext, useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import "./DashboardLayout.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    LayoutDashboard,
    Menu,
    Newspaper,
    PenSquare,
    Sparkles,
    UserRound,
    X,
    LogOut,
} from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import HeaderLogo from "../../assets/images/Header/logo.png";

export default function DashboardLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useContext(AuthContext);

    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const navItems = useMemo(
        () => [
            { to: "/dashboard", label: "Home", description: "Overview & shortcuts", Icon: LayoutDashboard },
            { to: "/dashboard/blog-list", label: "Blog posts", description: "List & manage", Icon: Newspaper },
            { to: "/dashboard/add-blog", label: "Create blog", description: "New article", Icon: PenSquare },
        ],
        []
    );

    const pageContext = useMemo(() => {
        const path = location.pathname;
        if (path === "/dashboard" || path === "/dashboard/") {
            return { title: "Home", kicker: "Overview" };
        }
        if (path.startsWith("/dashboard/blog-list")) {
            return { title: "Blog posts", kicker: "Library" };
        }
        if (path.startsWith("/dashboard/add-blog")) {
            return { title: "Create blog", kicker: "Editor" };
        }
        if (path.startsWith("/dashboard/edit-blog")) {
            return { title: "Edit blog", kicker: "Editor" };
        }
        if (path.startsWith("/dashboard/blog/")) {
            return { title: "Blog detail", kicker: "Preview" };
        }
        if (path.startsWith("/dashboard/profile")) {
            return { title: "Profile", kicker: "Account" };
        }
        return { title: "Dashboard", kicker: "" };
    }, [location.pathname]);

    return (
        <div className="dashboard-layout-container">
            <ToastContainer
                position="top-right"
                autoClose={2500}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <header className="dashboard-topbar">
                <div className="dashboard-topbar-accent" aria-hidden />
                <div className="dashboard-topbar-glow" aria-hidden />
                <div className="dashboard-topbar-inner">
                    <div className="dashboard-topbar-start">
                        <button
                            type="button"
                            className="dashboard-topbar-menu dashboard-mobile-only"
                            onClick={() => setSidebarOpen((v) => !v)}
                            aria-label={sidebarOpen ? "Close navigation" : "Open navigation"}
                            aria-expanded={sidebarOpen}
                        >
                            {sidebarOpen ? (
                                <X size={20} strokeWidth={2} aria-hidden />
                            ) : (
                                <Menu size={20} strokeWidth={2} aria-hidden />
                            )}
                        </button>

                        <div className="dashboard-topbar-brand">
                            <div className="dashboard-topbar-mark">
                                <img src={HeaderLogo} alt="Offshore Advisory logo" />
                            </div>
                            <div className="dashboard-topbar-titles">
                                <div className="dashboard-app-name">Offshore Advisory</div>
                                <div className="dashboard-app-meta">
                                    <span className="dashboard-app-subtitle">Admin</span>
                                    {pageContext.kicker ? (
                                        <>
                                            <span className="dashboard-meta-sep" aria-hidden>
                                                ·
                                            </span>
                                            <span className="dashboard-page-kicker">{pageContext.kicker}</span>
                                        </>
                                    ) : null}
                                    <span className="dashboard-meta-sep dashboard-meta-sep--wide" aria-hidden>
                                        /
                                    </span>
                                    <span className="dashboard-page-title" title={pageContext.title}>
                                        {pageContext.title}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-topbar-end">
                        <NavLink
                            to="/dashboard/profile"
                            className={({ isActive }) =>
                                `dashboard-topbar-profile ${isActive ? "is-active" : ""}`
                            }
                            aria-label="Open profile"
                            title="Profile"
                        >
                            <UserRound size={18} strokeWidth={2} aria-hidden />
                        </NavLink>
                    </div>
                </div>
            </header>

            <div className="dashboard-shell">
                <div
                    className={`dashboard-backdrop ${sidebarOpen ? "open" : ""}`}
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden={!sidebarOpen}
                />

                <aside className={`dashboard-sidebar ${sidebarOpen ? "open" : ""}`} aria-label="Main navigation">

                    <div className="dashboard-sidebar-header">
                        <div className="dashboard-brand">
                            <div className="dashboard-brand-mark" aria-hidden="true">
                                <img src={HeaderLogo} alt="" />
                            </div>
                            <div className="dashboard-brand-text">
                                <div className="dashboard-brand-title">Offshore Advisory</div>
                                <div className="dashboard-brand-tagline">
                                    <Sparkles size={11} strokeWidth={2} aria-hidden />
                                    Admin workspace
                                </div>
                            </div>
                        </div>
                    </div>

                    <nav className="dashboard-nav" aria-label="Primary">
                        <p className="dashboard-nav-label">Navigation</p>
                        <ul className="dashboard-nav-list">
                            {navItems.map((item) => {
                                const ItemIcon = item.Icon;
                                return (
                                    <li key={item.to}>
                                        <NavLink
                                            to={item.to}
                                            className={({ isActive }) =>
                                                `dashboard-nav-link ${isActive ? "active" : ""}`
                                            }
                                            end={item.to === "/dashboard"}
                                        >
                                            <span className="dashboard-nav-link-main">
                                                <span className="dashboard-nav-icon" aria-hidden>
                                                    <ItemIcon size={18} strokeWidth={2} />
                                                </span>
                                                <span className="dashboard-nav-copy">
                                                    <span className="dashboard-nav-title">{item.label}</span>
                                                    <span className="dashboard-nav-desc">{item.description}</span>
                                                </span>
                                            </span>
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    <div className="dashboard-sidebar-footer">
                        <p className="dashboard-sidebar-footer-label">Account</p>
                        <NavLink
                            to="/dashboard/profile"
                            className={({ isActive }) =>
                                `dashboard-footer-link dashboard-profile-link ${isActive ? "active" : ""}`
                            }
                        >
                            <UserRound className="dashboard-footer-icon" size={18} strokeWidth={2} aria-hidden />
                            <span className="dashboard-footer-copy">
                                <span className="dashboard-footer-title">Profile</span>
                                <span className="dashboard-footer-desc">Your details</span>
                            </span>
                        </NavLink>

                        <button type="button" className="dashboard-logout-link" onClick={handleLogout}>
                            <LogOut className="dashboard-footer-icon" size={18} strokeWidth={2} aria-hidden />
                            <span className="dashboard-footer-copy">
                                <span className="dashboard-footer-title">Log out</span>
                                <span className="dashboard-footer-desc">End session</span>
                            </span>
                        </button>
                    </div>
                </aside>

                <main className="dashboard-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
